// Call Handling — unified list of Ring Groups + Queues + Digital Receptionists
// (matches 3CX shape). Each row shows type suffix in parens. Toolbar has 3
// add buttons; row click navigates to the type-specific editor.

import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { matchesScope } from '../lib/tenantScope';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Search, MoreVertical } from 'lucide-react';
import { api, getToken } from '../lib/api';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

type Row = {
  id: string;
  type: 'Ring Group' | 'Queue' | 'Digital Receptionist';
  name: string;
  extension: string;
  department: string;
  dids: string;
  destination: string;
  domain_uuid: string;
  registered: boolean;
};

export default function RingGroups() {
  const me = api.me();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'Ring Group' | 'Queue' | 'Digital Receptionist'>('Ring Group');
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{top: number; left: number} | null>(null);

  // Ring Groups — use the global endpoint so the list still loads even if
  // me.domain_uuid is stale (e.g. JWT was issued before the user's domain
  // was set). Filter client-side by the user's domain when we know it.
  const ringGroupsQ = useQuery<any[]>({
    queryKey: ['ring-groups', me?.domain_uuid || 'all'],
    queryFn: async () => {
      const r = await fetch('/api/ring-groups', auth());
      if (!r.ok) return [];
      const rows: any[] = await r.json();
      // Normalize global shape ({id,name,extension}) → tenant shape used below.
      const norm = rows.map((x: any) => ({
        ring_group_uuid: x.ring_group_uuid || x.id,
        ring_group_name: x.ring_group_name || x.name,
        ring_group_extension: x.ring_group_extension || x.extension,
        domain_uuid: x.domain_uuid,
        dids: x.dids,
        no_answer_label: x.no_answer_label,
      }));
      // Operator console: show every tenant's ring groups (the global endpoint already returns all).
      return norm;
    },
  });
  // Digital Receptionists = IVR menus (/api/ivr → v_ivr_menus; superadmins get
  // every tenant's). Was /api/callflow — the Lua-script CRUD, always empty (#82).
  const callFlowsQ = useQuery<any[]>({
    queryKey: ['call-flows', me?.domain_uuid],
    queryFn: () => fetch('/api/ivr', auth()).then(r => r.ok ? r.json() : []).catch(() => []),
  });
  // Queues — backed by FS mod_callcenter; reachable via /api/tenants/<id>/queues
  // (best-effort — page renders empty if endpoint isn't wired yet).
  const queuesQ = useQuery<any[]>({
    queryKey: ['queues', me?.domain_uuid, me?.role],
    queryFn: async () => {
      // Operator console: aggregate queues across every tenant.
      const ts = await fetch('/api/tenants', auth()).then(r => r.ok ? r.json() : []).catch(() => []);
      const all = await Promise.all((ts || []).map((t: any) =>
        fetch(`/api/tenants/${t.domain_uuid}/queues`, auth()).then(r => r.ok ? r.json() : []).catch(() => [])));
      return all.flat();
    },
  });

  // Department lookup
  const tenantsQ = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fetch('/api/tenants', auth()).then(r => r.json()) });
  const deptName = (uuid: string) =>
    (tenantsQ.data || []).find((t: any) => t.domain_uuid === uuid)?.domain_description ||
    (tenantsQ.data || []).find((t: any) => t.domain_uuid === uuid)?.domain_name || '';

  // Combine into one shape
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const rg of (ringGroupsQ.data || [])) {
      out.push({
        id: rg.ring_group_uuid,
        type: 'Ring Group',
        name: rg.ring_group_name || '—',
        extension: rg.ring_group_extension || '',
        department: deptName(rg.domain_uuid || me?.domain_uuid || ''),
        dids: Array.isArray(rg.dids) ? rg.dids.join(', ') : (rg.dids || ''),
        destination: rg.no_answer_label || rg.destination_if_no_answer || rg.no_answer_target || '',
        domain_uuid: rg.domain_uuid || me?.domain_uuid || '',
        registered: true,
      });
    }
    for (const q of (queuesQ.data || [])) {
      out.push({
        id: q.queue_uuid,
        type: 'Queue',
        name: q.queue_name || '—',
        extension: q.queue_extension || '',
        department: deptName(q.domain_uuid || me?.domain_uuid || ''),
        dids: Array.isArray(q.dids) ? q.dids.join(', ') : (q.dids || ''),
        destination: q.timeout_destination || '',
        domain_uuid: q.domain_uuid || me?.domain_uuid || '',
        registered: true,
      });
    }
    for (const cf of (callFlowsQ.data || [])) {
      out.push({
        id: cf.callflow_uuid || cf.id,
        type: 'Digital Receptionist',
        name: cf.callflow_name || cf.name || '—',
        extension: cf.callflow_extension || cf.extension || '',
        department: deptName(cf.domain_uuid || me?.domain_uuid || ''),
        dids: Array.isArray(cf.dids) ? cf.dids.join(', ') : (cf.dids || ''),
        destination: cf.destination_after_timeout || '',
        domain_uuid: cf.domain_uuid || me?.domain_uuid || '',
        registered: true,
      });
    }
    return out;
  }, [ringGroupsQ.data, queuesQ.data, callFlowsQ.data, tenantsQ.data]);

  // Filter + search
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (!matchesScope(r.domain_uuid)) return false;
      if (r.type !== filter) return false;
      if (!q) return true;
      return [r.name, r.extension, r.department, r.dids].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [rows, search, filter]);

  // Per-type delete dispatch
  const remove = useMutation({
    mutationFn: async (r: Row) => {
      // Ring Group: use the global endpoint (DELETE /api/ring-groups/:id) — it
      // also clears v_ring_group_destinations, unlike the tenant-scoped one which
      // orphaned the member rows.
      const res = r.type === 'Ring Group'
          ? await fetch(`/api/ring-groups/${r.id}`, { method: 'DELETE', ...auth() })
        : r.type === 'Queue'
          ? await fetch(`/api/tenants/${r.domain_uuid}/queues/${r.id}`, { method: 'DELETE', ...auth() })
          : await fetch(`/api/ivr/${r.id}`, { method: 'DELETE', ...auth() });
      if (!res.ok) throw new Error((await res.text().catch(() => '')) || 'delete failed');
      return res;
    },
    onSuccess: (_res, r) => {
      toast('success', `${r.type} deleted`);
      qc.invalidateQueries({ queryKey: ['ring-groups', me?.domain_uuid] });
      qc.invalidateQueries({ queryKey: ['queues', me?.domain_uuid] });
      qc.invalidateQueries({ queryKey: ['call-flows', me?.domain_uuid] });
    },
    onError: (e: any) => toast('error', String(e?.message || e)),
  });

  function openRow(r: Row) {
    if (r.type === 'Ring Group')           navigate(`/ring-groups/${r.id}/edit`);
    if (r.type === 'Queue')                navigate(`/queues/${r.id}/edit`);
    if (r.type === 'Digital Receptionist') navigate(`/digital-receptionist/${r.id}/edit`);
  }

  function closeMenu() { setMenuFor(null); setMenuPos(null); }
  const menuRow = visible.find((r) => r.id === menuFor) || null;

  return (
    <div className="space-y-3">
      <h1 className="text-2xl font-semibold">Call Handling</h1>
      <div className="flex gap-1 border-b border-border">
        {([['Ring Group', 'Ring Groups'], ['Queue', 'Queues'], ['Digital Receptionist', 'Digital Receptionist']] as [any, string][]).map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${filter === key ? 'border-primary text-primary' : 'border-transparent text-textSecondary hover:text-text'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Toolbar — 3 add buttons + search */}
      <div className="card p-2 flex flex-wrap gap-1 items-center">
        {filter === 'Ring Group' && (
          <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => navigate('/ring-groups/new')}>
            <Plus size={14} /> Add Ring Group
          </button>
        )}
        {filter === 'Queue' && (
          <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => navigate('/queues/new')}>
            <Plus size={14} /> Add Queue
          </button>
        )}
        {filter === 'Digital Receptionist' && (
          <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => navigate('/digital-receptionist/new')}>
            <Plus size={14} /> Add Digital Receptionist
          </button>
        )}

        <div className="flex-1" />

        <div className="flex items-center gap-1 bg-elevated rounded-md px-2 py-1">
          <Search size={14} className="text-textSecondary" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search…"
            className="bg-transparent outline-none text-sm w-48"
          />
        </div>
      </div>

      {/* Unified list */}
      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Extension</th>
              <th>Department</th>
              <th>DIDs</th>
              <th>Destination if no answer</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={`${r.type}-${r.id}`} className="hover:bg-elevated cursor-pointer" onClick={() => openRow(r)}>
                <td>
                  <span className="inline-flex items-center gap-2">
                    <span className={`inline-block w-2.5 h-2.5 rounded-sm ${r.registered ? 'bg-success' : 'bg-muted/60'}`} />
                    {r.name}
                    <span className="text-textSecondary text-xs">({r.type})</span>
                  </span>
                </td>
                <td className="font-mono">{r.extension || '—'}</td>
                <td className="text-sm">{r.department}</td>
                <td className="text-sm">{r.dids}</td>
                <td className="text-sm">{r.destination || ''}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button className="btn-ghost p-1" title="Actions" onClick={(e) => {
                      if (menuFor === r.id) { closeMenu(); return; }
                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      const MENU_H = 88, MENU_W = 160; // Edit + Delete + padding
                      const room_below = window.innerHeight - rect.bottom;
                      const top = room_below >= MENU_H + 8 ? rect.bottom + 4 : Math.max(8, rect.top - MENU_H - 4);
                      const left = Math.min(rect.right - MENU_W, window.innerWidth - MENU_W - 8);
                      setMenuPos({ top, left });
                      setMenuFor(r.id);
                    }}>
                    <MoreVertical size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-textSecondary">
                {rows.length === 0 ? 'No ring groups, queues, or digital receptionists yet.' : 'No matches.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Row actions menu — rendered in a portal to <body> so the card's
          backdrop-filter (glass themes) can't become its containing block and
          clip the fixed-positioned menu. That clipping was why the three-dots
          menu "did nothing". A transparent backdrop closes it on outside click. */}
      {menuFor && menuRow && createPortal(
        <>
          <div className="fixed inset-0 z-40" onClick={closeMenu} />
          <div className="card z-50 min-w-[160px] py-1 shadow-lg"
               style={{ position: 'fixed', top: menuPos?.top ?? 0, left: menuPos?.left ?? 0 }}>
            <button className="w-full text-left px-3 py-2 hover:bg-elevated text-sm"
                    onClick={() => { const r = menuRow; closeMenu(); openRow(r); }}>Edit</button>
            <button className="w-full text-left px-3 py-2 hover:bg-danger/10 text-danger text-sm flex items-center gap-2"
                    onClick={() => { const r = menuRow; closeMenu(); if (confirm(`Delete ${r.type} "${r.name}"?`)) remove.mutate(r); }}>
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
